/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { unloadActionData } from '@ibm-watson/shared/modules/actions/store/actions/actions';
import { Defaults } from '@ibm-watson/shared/modules/channels/components/web-chat/messages';
import { settingsSchema } from '@ibm-watson/shared/modules/channels/components/web-chat/schema';
import {
  listIntegrations,
  updateIntegration
} from '@ibm-watson/shared/modules/channels/store/channels/actions';
import {
  selectWebchatDefaultPlanSettings,
  selectWebChatIntegrationsByEnvironment
} from '@ibm-watson/shared/modules/channels/store/channels/webchat-selectors';
import * as API from '@ibm-watson/shared/modules/ia/api/skills';
import {
  setAssistantId,
  setSkillId
} from '@ibm-watson/shared/modules/ia/store/session/actions';
import { configureDialogOrActionSkill } from '@ibm-watson/shared/modules/ia/store/skills/ducks/create';
import { ApiStatus } from '../../../../ts';
import { Assistant } from '../../../../ts/assistant';
import { Skill, SkillType } from '../../../../ts/skill';
import { CreateAsyncThunk } from '../../../../ts/state';
import { getErrorString } from '../../util/errorHandler';
import {
  createdObjectEvents,
  deletedObjectEvents
} from '../../util/trackers/segment';
import { createAssistant, deleteAssistant } from '../assistants/actions';
import { selectSkillReferencesFromAssistant } from '../assistants/core-selectors';
import { selectIsAccessUpdatesEnabled } from '../features/selectors';
import { addErrorMessage, addMessage, ToastStatus } from '../messages/actions';
import { updateSkill } from '../skills/actions';

type CreateNewIaAssistant = Partial<Assistant> & {
  parentPageName?: string;
};
/**
 * Automatically create two assistant with an action skill, web chat and preview link.
 */
export const createNewIAAssistant = createAsyncThunk<
  Assistant,
  CreateNewIaAssistant,
  CreateAsyncThunk
>(
  'onboarding/start',
  async (_params, { dispatch, getState }) => {
    // Save a reference to the created assistant in case it has to be deleted.
    let assistant: Assistant;
    let skill: Skill;

    const { parentPageName = 'create assistant wizard classic' } = _params;
    const previousParams = _.omit(_params, 'parentPageName');

    try {
      const params = {
        name: 'My first assistant',
        description: 'Built for you to explore and learn.',
        ...previousParams
      };

      assistant = await dispatch(createAssistant(params)).unwrap();

      const { assistant_id } = assistant;

      // The action skill is created automatically
      const skillId = selectSkillReferencesFromAssistant(assistant)?.find(
        skill => skill.type === SkillType.ACTION
      )?.skill_id;

      // Use revised API for Access Control improvements if enabled (this page only present for newIA)
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with true on call to list
      const useNewIAUpdatedAccess = selectIsAccessUpdatesEnabled(getState());
      // Update the actions skill with default content
      const response = await API.listSkills(
        {
          new_ia: true,
          assistant_id
        },
        useNewIAUpdatedAccess,
        assistant_id
      );

      skill = response.skills.find(skill => skill.skill_id === skillId);

      const modifiedSkill = await configureDialogOrActionSkill(
        {
          ...skill,
          sample: false,
          // Force the type to be "actions" so we retrieve the correct default actions.
          type: SkillType.ACTION
        },
        getState,
        dispatch
      );

      await dispatch(updateSkill(modifiedSkill)).unwrap();

      // Update web chat with default configuration since store defaults are invalid
      await dispatch(listIntegrations(assistant_id)).unwrap();

      // Loop through the environments and reset the web chat configuration
      // Web chat doesn't have a default configuration, so we need to create one.
      // FIXME: When https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/56589
      // is implemented, remove the reference to "linked_assistant".
      const updateWebchats = assistant.linked_assistant
        ? assistant.linked_assistant.map(environment => {
            const environmentId = environment.assistant_id;
            return dispatch(resetWebChatForEnvironment(environmentId));
          })
        : assistant.assistant_environments.map(environment => {
            const environmentId = environment.environment_id;
            return dispatch(resetWebChatForEnvironment(environmentId));
          });

      await Promise.all(updateWebchats);

      // To prevent other components from re-rendering with stale data,
      // set the correct assistant ID, clear all the actions data, and
      // clear out the skill ID. When the assistant component detects that
      // a change in assistant has occurred, it will trigger the retrieval
      // of new skill data and eventually it will set the correct skill ID.
      dispatch(setAssistantId(assistant_id));
      dispatch(unloadActionData());
      dispatch(setSkillId(undefined));

      // If assistant is created from the wizard, hide the creation message.
      const wizardPageName = 'Wizard newia 2.0';
      if (parentPageName !== wizardPageName) {
        await dispatch(
          addMessage({
            message: `${params.name} created successfully.`,
            status: ToastStatus.SUCCESS
          })
        );
      }

      createdObjectEvents.assistant({
        object: assistant.assistant_id,
        parentPageName,
        resultValue: 'success',
        successFlag: true
      });

      return assistant;
    } catch (error) {
      // Delete assistant if created
      if (assistant) {
        await dispatch(deleteAssistant(assistant.assistant_id));
      }

      dispatch(addErrorMessage(error));

      createdObjectEvents.assistant({
        object: null,
        parentPageName,
        resultValue: getErrorString(error),
        successFlag: false
      });
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const { onboarding } = getState();
      return onboarding.status !== ApiStatus.LOADING;
    }
  }
);

/**
 * Deletes the assistant along with its associated live environment, action skill, and integrations.
 * Note: Store deletes all the related integrations in an assistant
 */
export const deleteNewIAAssistant = createAsyncThunk<
  string,
  Pick<Assistant, 'name' | 'assistant_id'>,
  CreateAsyncThunk
>('onboarding/delete', async ({ assistant_id, name }, { dispatch }) => {
  try {
    // Delete the assistant
    await dispatch(deleteAssistant(assistant_id)).unwrap();
    dispatch(
      addMessage({
        message: `${name} deleted successfully.`,
        status: ToastStatus.SUCCESS
      })
    );
    deletedObjectEvents.assistant({
      object: assistant_id,
      resultValue: 'success',
      successFlag: true
    });
    return assistant_id;
  } catch (error) {
    deletedObjectEvents.assistant({
      object: assistant_id,
      resultValue: getErrorString(error),
      successFlag: false
    });

    dispatch(addErrorMessage(error));
    throw error;
  }
});

/**
 * Resets the web chat for the given environment to the default configuration
 */
export const resetWebChatForEnvironment = createAsyncThunk<
  void,
  string,
  CreateAsyncThunk
>('onboarding/reset-webchat', async (environmentID, { dispatch, getState }) => {
  try {
    // Select the first webchat for the given environment
    const webChat = selectWebChatIntegrationsByEnvironment(
      getState(),
      environmentID
    )[0];

    if (!webChat) {
      return;
    }

    // Provide a default config so that the text of the default starter actions
    // in the web chat home screen is prepended with the string "Example: "
    // so that the user is not surprised when said actions do not work.
    // NOTE: Overriding home screen settings can be done away altogether when the default
    // web chat home screen actions are in sync with the default actions,
    // such that they will work out-of-the-box OR when home screen configuration is moved
    // directly into actions.
    const defaultWebchatConfig = settingsSchema.cast({
      ...selectWebchatDefaultPlanSettings(getState()),
      home_screen: {
        is_on: true,
        starters: {
          buttons: [
            {
              label: `${Defaults.starter0.defaultMessage}`,
              value: ''
            },
            {
              label: `${Defaults.starter1.defaultMessage}`,
              value: ''
            },
            {
              label: `${Defaults.starter2.defaultMessage}`,
              value: ''
            }
          ]
        }
      }
    });

    await dispatch(
      updateIntegration({
        assistantId: webChat.assistant_id,
        environmentId: webChat.environment_id,
        integration: {
          ...webChat,
          settings: defaultWebchatConfig
        }
      })
    );
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});
